// Tickets main pane — header (page title + new ticket + sync) + body.

import { GITHUB_SYNC_URL, GITHUB_SYNC_HEADERS } from '../../config.js'
import { freshTicket } from '../../functions/tickets/ticketUtils.js'

const PAGE_TITLES = {
  '/tickets': 'My view',
  '/tickets/my': 'My view',
  '/tickets/general': 'General',
  '/tickets/feed': 'Feed',
  '/tickets/canvas': 'Canvas',
  '/tickets/epics': 'Epics',
  '/tickets/stories': 'Stories',
  '/tickets/board': 'Board',
  '/tickets/standups': 'Standups',
  '/tickets/requests': 'Requests',
  '/tickets/requests/inbound': 'Inbound',
  '/tickets/requests/outbound': 'Outbound',
  '/tickets/requests/audit': 'Audit / Reports',
  '/tickets/resolutions': 'Resolutions',
  '/tickets/resolutions/improvements': 'Improvement Requests',
  '/tickets/resolutions/edit-requests': 'Edit Requests'
}

export const TicketsMain = {
  extends: 'AppMain',

  Header: { extends: 'TicketsHeader' },

  Body: {
    // Board page title — provides the required H1 landmark for screen readers.
    // Visually matches the compact top-bar style used by CanvasMain.
    BoardTitleBar: {
      flow: 'x',
      align: 'center left',
      padding: 'Z A',
      borderBottomWidth: 'V',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',

      PageTitle: {
        tag: 'h1',
        margin: '0',
        fontSize: 'B',
        fontWeight: '700',
        letterSpacing: '-.02em',
        color: 'title',
        text: 'Board'
      }
    },

    TicketsBoardView: {}
  }
}

export const TicketsHeader = {
  extends: 'PageHeader',
  backdropFilter: 'blur(20px)',

  LeftGroup: {
    PageTitle: {
      tag: 'span',
      fontSize: 'B',
      fontWeight: '600',
      letterSpacing: '-.015em',
      color: 'title',
      text: (el, s) => PAGE_TITLES[s.root.currentPath || s.root.route] || 'Tickets'
    }
  },

  CenterGroup: {
    // Cross-page assignee filter — reused by every /tickets/* page.
    // State: root.filterAssignee ∈ { null | 'team' | 'agents' | 'me' }.
    // null = All (default); 'team' = humans except current user; 'agents'
    // = *-agent@symbols.app; 'me' = current user.
    FilterSwitcher: {
      extends: 'SharedViewSwitcher',
      stateKey: 'filterAssignee',

      All:    { extends: 'SharedViewSwitcherBtn', value: null,     label: 'All' },
      Team:   { extends: 'SharedViewSwitcherBtn', value: 'team',   label: 'Team' },
      Agents: { extends: 'SharedViewSwitcherBtn', value: 'agents', label: 'Agents' },
      Mine:   { extends: 'SharedViewSwitcherBtn', value: 'me',     label: 'Me' }
    }
  },

  RightGroup: {
    // OnlineBar / AiToggle / Profile already live in the global AppHeader.
    // Tickets-only actions: new-ticket button + GitHub resync.
    NewBtn: {
      tag: 'button',
      flow: 'x',
      align: 'center center',
      gap: 'Z',
      padding: 'Z A',
      round: 'C',
      border: 'none',
      cursor: 'pointer',
      fontSize: 'Z2',
      fontWeight: '600',
      theme: 'primary',
      onClick: (e, el, s) => {
        s.root.update({
          selectedTicket: { _new: true },
          ticketDraft: freshTicket()
        })
      },
      Icon: { icon: 'plus', boxSize: 'A' },
      Label: { tag: 'span', text: 'New ticket' }
    },

    // Manual GitHub Projects resync — pokes the github-sync Edge Function
    // (pull + push pass) and reloads. Spins the icon while in flight.
    ResyncBtn: {
      extends: 'IconButton',
      Icon: { icon: 'refresh' },
      title: 'Resync with GitHub Projects',
      transform: (el, s) =>
        s.root.syncing ? 'rotate(360deg)' : 'rotate(0deg)',
      transition: 'transform 1s linear',
      onClick: async (e, el, s) => {
        const root = s.root
        if (root.syncing) return
        root.update({ syncing: true })
        try {
          await fetch(GITHUB_SYNC_URL, {
            method: 'POST',
            headers: GITHUB_SYNC_HEADERS
          })
        } catch (_) {
          /* still reload to pick up any partial pull */
        }
        if (typeof window !== 'undefined') window.location.reload()
      }
    }
  }
}

export const TicketsHeaderFilter = {
  tag: 'button',
  type: 'button',
  padding: 'Z A',
  round: 'C',
  border: 'none',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: (el, s) => (s.root?.filterAssignee || 'all') === el.value ? '600' : '400',
  theme: (el, s) => (s.root?.filterAssignee || 'all') === el.value ? 'secondary' : 'tertiary',
  onClick: (e, el, s) => {
    s.root.update({ filterAssignee: el.value === 'all' ? null : el.value })
    try { el.call('reloadTickets') } catch (err) { console.error(err) }
  }
}
